/**
 * ParticleCanvas — runtime canvas backing the canvas-rendered ambient
 * effects (`floating-heart-bokeh` + the 14 others).
 *
 * One `<canvas>` per effect code. Resizes on window resize. Mirrors
 * pointer input for `sparkler-trail-hearts`. Honours `prefers-reduced-motion`.
 *
 * Note: the CSS-only `ambient-orbs` effect doesn't go through this
 * component — `ParticleOverlay` short-circuits to `AmbientOrbsOverlay`
 * instead.
 */
import { useEffect, useRef } from 'react';
import type { CanvasEffectId, CanvasState, PointerSample } from './types';
import { createEmptyCanvasState } from './types';
import { drawEffect, initializeEffect } from './effects';
import { spawnSparkHearts } from './effects/initializers';

interface ParticleCanvasProps {
  effect: CanvasEffectId;
  reducedMotion: boolean;
}

export function ParticleCanvas({ effect, reducedMotion }: ParticleCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) return;

    let frame = 0;
    let running = true;
    let lastTime = performance.now();
    const pointer: PointerSample = {
      x: 0,
      y: 0,
      active: false,
      time: 0,
    };
    const state: CanvasState = createEmptyCanvasState();

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const width = Math.max(1, Math.floor(rect.width));
      const height = Math.max(1, Math.floor(rect.height));
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      state.width = width;
      state.height = height;
      state.dpr = dpr;
      initializeEffect(effect, state, reducedMotion);
    };

    const updatePointer = (event: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      if (x < 0 || y < 0 || x > rect.width || y > rect.height) {
        pointer.active = false;
        return;
      }

      pointer.x = x;
      pointer.y = y;
      pointer.active = true;
      pointer.time = performance.now();

      if (effect === 'sparkler-trail-hearts' && !reducedMotion) {
        spawnSparkHearts(state, x, y, 5);
      }
    };

    const pressPointer = (event: PointerEvent) => {
      updatePointer(event);
      if (effect === 'sparkler-trail-hearts' && !reducedMotion) {
        spawnSparkHearts(state, pointer.x, pointer.y, 26);
      }
    };

    const render = (now: number) => {
      const dt = reducedMotion ? 0 : Math.min((now - lastTime) / 16.67, 2.4);
      lastTime = now;
      drawEffect(ctx, state, effect, now / 1000, dt, pointer, reducedMotion);
      if (running && !reducedMotion) {
        frame = requestAnimationFrame(render);
      }
    };

    resize();
    render(performance.now());

    window.addEventListener('resize', resize);
    window.addEventListener('pointermove', updatePointer);
    window.addEventListener('pointerdown', pressPointer);

    return () => {
      running = false;
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', resize);
      window.removeEventListener('pointermove', updatePointer);
      window.removeEventListener('pointerdown', pressPointer);
    };
  }, [effect, reducedMotion]);

  return (
    <canvas
      aria-hidden="true"
      className="absolute inset-0 h-full w-full"
      ref={canvasRef}
    />
  );
}
